// The two route surfaces of a logistics calculation: a blank document being
// drawn up, and a saved one being read or corrected. Both are full pages —
// the document is the screen, not something a drawer shows a preview of.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router';
import {
  LogisticsCalculationCargoContentSchema,
  LogisticsCargoCandidateSchema,
} from '@magimp/shared';
import { hasPermission, useAuthStore } from '../../../stores/auth.js';
import { useToastStore } from '../../../stores/toast.js';
import { EditorPage, useEditorGuard } from '../../../ui/index.js';
import {
  LogisticsCalculationFormSchema,
  candidateToCargo,
  emptyLogisticsCalculation,
  heldCargoOf,
  toWireContent,
  type LogisticsCalculationFormValues,
} from '../form-model.js';
import {
  useCreateLogisticsCalculation,
  useLogisticsCalculationDetail,
  useLogisticsCargoCandidate,
} from '../queries.js';
import { DocumentToolbar } from './DocumentToolbar.js';
import { LogisticsCalculationForm } from './LogisticsCalculationForm.js';
import {
  LogisticsCalculationEditHost,
  LogisticsCalculationView,
} from './LogisticsCalculationView.js';

type Form = LogisticsCalculationFormValues;
type DraftCargo = Form['cargos'][number];
/** What the create page knows about a cargo another screen sent it to. */
type Prefill = { cargo: DraftCargo | null; notice: string | undefined };

function useBackToList() {
  const navigate = useNavigate();
  return useCallback(() => navigate('/ttn'), [navigate]);
}

export function LogisticsCalculationCreatePage() {
  const user = useAuthStore((state) => state.user);
  const location = useLocation();
  const back = useBackToList();
  const prefill = useDraftPrefill(location.state);
  if (!user) return <PageState kind="loading" onBack={back} />;
  return (
    <CreateHost
      manager={{ name: user.name, phone: user.phone ?? '' }}
      prefill={prefill}
      back={back}
    />
  );
}

/**
 * The cargo the create page opens with, once the registry has answered for it.
 *
 * INVARIANT: nothing is materialised before the answer. A cargo another
 * document already holds must never appear as an ordinary row of this one, and
 * the row the caller handed over is text — only the registry knows whether it
 * is still free, still visible, and still that cargo.
 */
function useDraftPrefill(state: unknown): Prefill {
  const { t } = useTranslation('logisticsCalculations');
  const permissions = useAuthStore((store) => store.permissions);
  const candidate = useMemo(() => readDraftCandidateId(state), [state]);
  const stored = useMemo(
    () => readDraftCargo(state, hasPermission(permissions, 'cargo.chinaPayment.manage')),
    [state, permissions],
  );
  const resolved = useLogisticsCargoCandidate(candidate);
  const found = resolved.data?.items.find((item) => item.id === candidate);
  const cargo = useMemo(() => (found && !found.heldBy ? candidateToCargo(found) : null), [found]);
  if (candidate === undefined) return { cargo: stored, notice: undefined };
  if (resolved.isPending) return { cargo: null, notice: undefined };
  if (!found) return { cargo: null, notice: t('editor.prefill.unchecked') };
  if (found.heldBy) {
    return { cargo: null, notice: t('editor.prefill.held', { code: found.heldBy.publicCode }) };
  }
  return { cargo, notice: undefined };
}

export function LogisticsCalculationDetailPage() {
  const { id } = useParams<{ id: string }>();
  const back = useBackToList();
  const query = useLogisticsCalculationDetail(id ?? '');
  const [editing, setEditing] = useState(false);
  const stopEditing = useCallback(() => setEditing(false), []);
  if (!id) return <PageState kind="error" onBack={back} />;
  if (query.isLoading) return <PageState kind="loading" onBack={back} />;
  if (query.isError || !query.data) return <PageState kind="error" onBack={back} />;
  return editing ? (
    <LogisticsCalculationEditHost detail={query.data} onDone={stopEditing} onBack={back} />
  ) : (
    <LogisticsCalculationView detail={query.data} onEdit={() => setEditing(true)} onBack={back} />
  );
}

function PageState({ kind, onBack }: { kind: 'loading' | 'error'; onBack: () => void }) {
  const { t } = useTranslation('logisticsCalculations');
  return (
    <div className="doc-page">
      <DocumentToolbar onBack={onBack} />
      <p className="doc-page-state">{t(kind)}</p>
    </div>
  );
}

function CreateHost({
  manager,
  prefill,
  back,
}: {
  manager: { name: string; phone: string };
  prefill: Prefill;
  back: () => void;
}) {
  const [defaultValues] = useState(() => emptyLogisticsCalculation(manager));
  const form = useForm<Form>({
    resolver: zodResolver(LogisticsCalculationFormSchema),
    defaultValues,
  });
  const draftCargo = prefill.cargo;
  useEffect(() => {
    form.reset(draftCargo ? { ...form.getValues(), cargos: [draftCargo] } : defaultValues, {
      keepDefaultValues: true,
    });
  }, [defaultValues, draftCargo, form]);
  const create = useCreateLogisticsCalculation();
  return (
    <EditorPage dirty={form.formState.isDirty} busy={create.isPending} onConfirmClose={back}>
      <CreateBody form={form} create={create} notice={prefill.notice} back={back} />
    </EditorPage>
  );
}

function CreateBody({
  form,
  create,
  notice,
  back,
}: {
  form: ReturnType<typeof useForm<Form>>;
  create: ReturnType<typeof useCreateLogisticsCalculation>;
  notice: string | undefined;
  back: () => void;
}) {
  const { t } = useTranslation('logisticsCalculations');
  const navigate = useNavigate();
  const { bypassNext, confirmDestructive } = useEditorGuard();
  const showToast = useToastStore((state) => state.show);
  return (
    <LogisticsCalculationForm
      form={form}
      notice={notice}
      busy={create.isPending}
      submitLabel={create.isPending ? t('actions.creating') : t('actions.createDraft')}
      // The rejection travels on: a held cargo is named and marked by the form,
      // which owns the row, and every other failure is this page's to report.
      onSubmit={(content) =>
        create.mutateAsync({ content: toWireContent(content) }).then(
          (response) => {
            bypassNext();
            navigate(`/ttn/${encodeURIComponent(response.calculation.id)}`);
          },
          (error: unknown) => {
            if (!heldCargoOf(error)) showToast({ tone: 'error', text: t('editor.createError') });
            throw error;
          },
        )
      }
      onCancel={() => confirmDestructive(back)}
      onBack={back}
    />
  );
}

function readDraftCandidateId(state: unknown): string | undefined {
  if (typeof state !== 'object' || state === null) return undefined;
  if (!('logisticsCalculationDraftCandidate' in state)) return undefined;
  const candidate = LogisticsCargoCandidateSchema.safeParse(
    state.logisticsCalculationDraftCandidate,
  );
  return candidate.success ? candidate.data.id : undefined;
}

function readDraftCargo(state: unknown, includeInternalCosts: boolean): DraftCargo | null {
  if (typeof state !== 'object' || state === null) return null;
  if (!('logisticsCalculationDraftCargo' in state)) return null;
  const parsed = LogisticsCalculationCargoContentSchema.safeParse(
    state.logisticsCalculationDraftCargo,
  );
  if (!parsed.success) return null;
  return includeInternalCosts ? parsed.data : { ...parsed.data, lines: [] };
}
